import { useAction, useAtom } from '@reatom/npm-react'
import { useContext, useEffect, useMemo } from 'react'
import { useLatest } from 'react-use'
import styled from 'styled-components'

import { CircularLoader } from '@blade/ui-kit'

import { pinInputReatom } from './atoms'
import { PinDots } from './pin-dots'
import { PinInputContext } from './pin-input-context'
import { PinKeyboard } from './pin-keyboard'

import type { PinState } from './pin-dots'
import type { PinInputContextValue } from './pin-input-context'
import type { ChildrenProps } from '@root/shared/react'

export type PinInputProps = {
  length?: number
  onFilled: (value: string) => void
  state: PinState
  onResetState?: () => void
} & ChildrenProps

const PinInputRoot: React.FC<PinInputProps> = ({
  state,
  onFilled,
  onResetState,
  children,
  length = 6,
}) => {
  const onFilledRef = useLatest(onFilled)
  const onResetRef = useLatest(onResetState)

  const model = useMemo(
    () =>
      pinInputReatom(
        (value) => onFilledRef.current(value),
        () => onResetRef.current?.(),
        length,
      ),
    [length, onFilledRef, onResetRef],
  )
  const handleStateChange = useAction(model.handleStateChangeAction)

  useEffect(() => {
    handleStateChange(state)
  }, [handleStateChange, state])

  const contextValue = useMemo(
    (): PinInputContextValue => ({
      length: length,
      model: model,
      state: state,
    }),
    [length, model, state],
  )

  return (
    <PinInputContext.Provider value={contextValue}>
      {children}
    </PinInputContext.Provider>
  )
}

const Dots: React.FC = () => {
  const { state, model, length } = useContext(PinInputContext)

  const [value] = useAtom(model.valueAtom)

  return (
    <DotsContainer>
      <LoaderPlaceholder />
      <PinDots state={state} fillCount={value.length} length={length} />
      {state === 'loading' ? (
        <CircularLoader size={24} />
      ) : (
        <LoaderPlaceholder />
      )}
    </DotsContainer>
  )
}

const LoaderPlaceholder = styled.div`
  width: 24px;
  height: 24px;
`

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;

  align-items: center;
  gap: 16px;
`

const Keyboard: React.FC = () => {
  const { state, model } = useContext(PinInputContext)

  const handleBackspace = useAction(model.handleBackspaceAction)
  const handleInput = useAction(model.handleInputAction)

  return (
    <KeyboardContainer>
      <PinKeyboard
        onRemove={() => {
          handleBackspace(state)
        }}
        onInput={(digit) => {
          handleInput(digit, state)
        }}
      />
    </KeyboardContainer>
  )
}

const KeyboardContainer = styled.div`
  display: flex;
  justify-content: center;
`

type PinInputCompound = typeof PinInputRoot & {
  Dots: typeof Dots
  Keyboard: typeof Keyboard
}

const PinInput = PinInputRoot as PinInputCompound
PinInput.Dots = Dots
PinInput.Keyboard = Keyboard

export { PinInput }
